import type { BuiltInTool, BuiltInToolError, JsonSchema } from './types'

/**
 * #1545: an enterprise's teams, for a person's own agent: the portal's team forms as one tool per `/v1/teams…`
 * route, each under the scope that route needs (`teams:read` to read, `teams:write` to change). The MCP server
 * serves them as they stand (#1011) and chat offers them too, asking before every write (#321 decisions 4 and 5).
 *
 * None lists `run` among its callers, so a task cannot declare one (`tool_not_run_callable`) and none is re-pointed
 * at a run-scoped endpoint: inside a run an org change stays a plan a person signs off (#217, #1571). A model that
 * proposes a team change starts the built-in `team-create`, `team-update` or `team-delete` task, with its CEO gate.
 *
 * A team's charter is a structured value, and its argument waits on #1433. The schemas are closed, so a charter
 * passed today is refused by name, and adding the argument later changes nothing else.
 */

const TEAM_NOT_FOUND: BuiltInToolError = {
    code: 'team_not_found',
    when: "`id` names no team in this enterprise, another enterprise's included.",
    handling: 'tool_error',
}

const TEAM_SLUG_TAKEN: BuiltInToolError = {
    code: 'team_slug_taken',
    when: 'Another team in this enterprise already has the slug made from that name, so a repeat of a create is refused too.',
    handling: 'tool_error',
}

const MEMBER_NOT_FOUND: BuiltInToolError = {
    code: 'member_not_found',
    when: '`memberId` names no member in this enterprise.',
    handling: 'tool_error',
}

const CONCURRENT_RUN_LIMIT_INVALID: BuiltInToolError = {
    code: 'concurrent_run_limit_invalid',
    when: '`concurrentRunLimit` is not a whole number of at least 1.',
    handling: 'tool_error',
}

const CONCURRENT_RUN_LIMIT_OVER_ENTERPRISE: BuiltInToolError = {
    code: 'concurrent_run_limit_over_enterprise',
    when: "Every team's limit together would exceed the enterprise's own concurrent run limit. The refusal says by how much.",
    handling: 'tool_error',
}

const TEAM_ID: JsonSchema = { type: 'string', description: 'The team, by its id from os.list_teams.' }

/**
 * The simple fields a team is set by, as `POST /v1/teams` and `PATCH /v1/teams/:id` take them. The charter joins
 * them when #1433 lands.
 */
function teamFields(change: boolean): Record<string, JsonSchema> {
    return {
        name: {
            type: 'string',
            description: change
                ? 'The team’s new name, up to 200 characters. Its slug stays as it is.'
                : 'The team’s name, up to 200 characters. Its slug is made from it.',
        },
        concurrentRunLimit: {
            type: 'integer',
            description: change
                ? 'How many of the team’s runs may run at once, at least 1. Every team’s limit together stays within the enterprise’s.'
                : 'How many of the team’s runs may run at once, at least 1. Left out, the team uses the enterprise’s limit.',
            minimum: 1,
        },
    }
}

export const OS_TEAM_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.list_teams',
        family: 'os.org',
        summary: "The enterprise's teams, by name.",
        description:
            "List the enterprise's teams, ordered by name: each one's id, name, slug, status, lead (leadMemberId and " +
            "leadName, null for none), charter and concurrentRunLimit (null when it uses the enterprise's). No " +
            "arguments. Never another enterprise's teams.",
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        returns:
            'One row per team, each with `id`, `name`, `slug`, `status`, `leadMemberId`, `leadName` and `concurrentRunLimit`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        api: { method: 'GET', path: '/v1/teams', scope: 'teams:read' },
        examples: [{ input: {} }],
        public: true,
        chatPrompt: "os.list_teams lists the enterprise's teams.",
    },
    {
        name: 'os.read_team',
        family: 'os.org',
        summary: 'One team, with its members and the tasks its members’ roles run.',
        description:
            'Read one team: its name, slug, status, lead, charter and concurrentRunLimit, the members on it (members) ' +
            'and the tasks it runs (tasks): the tasks of the roles its members hold, seated or as lead. A task belongs ' +
            'to a role, not a team, so the list changes as members join and leave. Not found for a team in another ' +
            'enterprise.',
        inputSchema: {
            type: 'object',
            properties: { id: TEAM_ID },
            required: ['id'],
            additionalProperties: false,
        },
        returns:
            'The team: `id`, `name`, `slug`, `concurrentRunLimit` and the rest, `members` (who is on it) and `tasks` (the tasks of the roles its members hold).',
        callers: ['chat', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [TEAM_NOT_FOUND],
        api: { method: 'GET', path: '/v1/teams/:id', scope: 'teams:read' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000021' } }],
        public: true,
        chatPrompt: 'os.read_team reads one team, with who is on it and the tasks its members’ roles run.',
    },
    {
        name: 'os.create_team',
        family: 'os.org',
        summary: 'Create a team, optionally with its own concurrent run limit.',
        description:
            'Create a team with a name and, optionally, its own concurrent run limit (concurrentRunLimit); left out, ' +
            "the team uses the enterprise's. Its slug is made from the name, so a name whose slug another team " +
            'already has is refused with team_slug_taken. The response is the team as created.',
        inputSchema: {
            type: 'object',
            properties: teamFields(false),
            required: ['name'],
            additionalProperties: false,
        },
        returns: 'The team as created: `id`, `name`, `slug`, `concurrentRunLimit` and the rest, `members` and `tasks`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'unsafe',
        retryNote:
            'Every call creates a team. A repeat with the same name is refused (team_slug_taken) rather than creating a second.',
        errors: [TEAM_SLUG_TAKEN, CONCURRENT_RUN_LIMIT_INVALID, CONCURRENT_RUN_LIMIT_OVER_ENTERPRISE],
        api: { method: 'POST', path: '/v1/teams', scope: 'teams:write' },
        examples: [{ input: { name: 'Support', concurrentRunLimit: 2 } }],
        public: true,
        chatPrompt: 'os.create_team creates a team. It asks the person first.',
    },
    {
        name: 'os.update_team',
        family: 'os.org',
        summary: "Change a team's name or concurrent run limit.",
        description:
            "Change a team's name or its concurrent run limit (concurrentRunLimit); a field left out keeps its value. " +
            "A limit that would take every team's together past the enterprise's is refused with " +
            'concurrent_run_limit_over_enterprise. The response is the team after the change.',
        inputSchema: {
            type: 'object',
            properties: { id: TEAM_ID, ...teamFields(true) },
            required: ['id'],
            additionalProperties: false,
        },
        returns:
            'The team after the change: `id`, `name`, `slug`, `concurrentRunLimit` and the rest, `members` and `tasks`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'The same fields set twice leave the team as the first call did.',
        errors: [TEAM_NOT_FOUND, CONCURRENT_RUN_LIMIT_INVALID, CONCURRENT_RUN_LIMIT_OVER_ENTERPRISE],
        api: { method: 'PATCH', path: '/v1/teams/:id', scope: 'teams:write' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000021', name: 'Customer support' } }],
        public: true,
        chatPrompt: "os.update_team changes a team's name or concurrent run limit. It asks the person first.",
    },
    {
        name: 'os.delete_team',
        family: 'os.org',
        summary: 'Remove a team.',
        description:
            'Remove a team. Its members come off it and keep their roles, and with them the tasks those roles run. The enterprise’s last team is refused with ' +
            'last_team: an enterprise keeps at least one. Any other refusal comes back with the API’s reason. The ' +
            'call is the confirmation, so the tool takes no confirm argument.',
        inputSchema: {
            type: 'object',
            properties: { id: TEAM_ID },
            required: ['id'],
            additionalProperties: false,
        },
        returns: 'Nothing: the team is gone.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'A repeat finds the team already gone and is refused with team_not_found, removing nothing else.',
        errors: [
            TEAM_NOT_FOUND,
            {
                code: 'last_team',
                when: 'The team is the last one the enterprise has.',
                handling: 'tool_error',
            },
        ],
        // The route takes no `confirm`: over MCP and chat the call is the intent, and chat asks first (#1545).
        api: { method: 'DELETE', path: '/v1/teams/:id', scope: 'teams:write' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000021' } }],
        public: true,
        chatPrompt: 'os.delete_team removes a team. It asks the person first.',
    },
    {
        name: 'os.add_team_member',
        family: 'os.org',
        summary: 'Put a member on a team, optionally in a role.',
        description:
            'Put a member on a team, and, when roleId names one, give them that role too. The role is a convenience: ' +
            'a team can hold a member before it has a role to give them. The response is the team after the change.',
        inputSchema: {
            type: 'object',
            properties: {
                id: TEAM_ID,
                memberId: { type: 'string', description: 'The member to put on the team.' },
                roleId: {
                    type: 'string',
                    description: 'A role the member holds from now on as well. Left out, their roles are unchanged.',
                },
            },
            required: ['id', 'memberId'],
            additionalProperties: false,
        },
        returns: 'The team after the change, with the member among its `members`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'A member already on the team stays on it once, and holds a role it names once.',
        errors: [
            TEAM_NOT_FOUND,
            MEMBER_NOT_FOUND,
            {
                code: 'role_not_found',
                when: '`roleId` names no role in this enterprise.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/teams/:id/members', scope: 'teams:write' },
        examples: [
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000021',
                    memberId: '5f0c1d2e-0000-4000-8000-000000000014',
                },
            },
        ],
        public: true,
        chatPrompt: 'os.add_team_member puts a member on a team. It asks the person first.',
    },
    {
        name: 'os.remove_team_member',
        family: 'os.org',
        summary: 'Take a member off a team.',
        description:
            'Take a member off a team. They keep their roles; a lead taken off no longer leads it. The response is ' +
            'the team after the change.',
        inputSchema: {
            type: 'object',
            properties: {
                id: TEAM_ID,
                memberId: { type: 'string', description: 'The member to take off the team.' },
            },
            required: ['id', 'memberId'],
            additionalProperties: false,
        },
        returns: 'The team after the change, without the member among its `members`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'A member already off the team stays off it.',
        errors: [TEAM_NOT_FOUND, MEMBER_NOT_FOUND],
        api: { method: 'DELETE', path: '/v1/teams/:id/members/:memberId', scope: 'teams:write' },
        examples: [
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000021',
                    memberId: '5f0c1d2e-0000-4000-8000-000000000014',
                },
            },
        ],
        public: true,
        chatPrompt: 'os.remove_team_member takes a member off a team. It asks the person first.',
    },
]
